/**
 * Date formatting utilities for the eSign Platform portal.
 * All server dates are stored in UTC.
 *
 * Display format:
 * - If tenant timezone is set: tenant TZ primary, UTC secondary (on new line, smaller, lighter).
 * - If not set (UTC): single UTC line only.
 * - If tenant TZ == UTC: single UTC line only (no duplicate).
 */

// ── Tenant timezone state ──
let _tenantTimeZone = null;

/**
 * Set the tenant timezone (IANA ID like "America/Chicago").
 * Called after loading branding settings.
 */
export function setTenantTimeZone(tz) {
    _tenantTimeZone = tz || null;
}

/**
 * Get the current tenant timezone.
 */
export function getTenantTimeZone() {
    return _tenantTimeZone;
}

/**
 * Normalize a server datetime string to ensure UTC parsing.
 * .NET serializes DateTime with Kind=Unspecified (no 'Z' suffix),
 * which JavaScript interprets as local time. Append 'Z' so it parses as UTC.
 */
function normalizeUtcDateStr(dateStr) {
    if (typeof dateStr === 'string' && dateStr.includes('T') &&
        !dateStr.endsWith('Z') && !/[+-]\d{2}:\d{2}$/.test(dateStr)) {
        return dateStr + 'Z';
    }
    return dateStr;
}

/**
 * Get the timezone abbreviation for a given date and IANA timezone.
 */
function getTzAbbr(date, timeZone) {
    try {
        return Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'short' })
            .formatToParts(date)
            .find(p => p.type === 'timeZoneName')?.value || '';
    } catch {
        return '';
    }
}

/** True when the tenant timezone is effectively UTC (null, empty, or literal "UTC"). */
function isTenantUtc() {
    return !_tenantTimeZone || _tenantTimeZone === 'UTC' || _tenantTimeZone === 'Etc/UTC';
}

const DATE_TIME_OPTS = {
    year: 'numeric', month: 'numeric', day: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true
};

/**
 * Format a UTC datetime for display (returns HTML).
 *
 * If tenant TZ is UTC (or not set): single line "M/D/YYYY h:mm AM UTC"
 * If tenant TZ differs from UTC:
 *   Primary:   "M/D/YYYY h:mm AM CST"
 *   Secondary: "<span class='dt-secondary'>M/D/YYYY h:mm AM UTC</span>"
 */
export function formatDateTime(dateStr) {
    if (!dateStr) return '';
    const date = new Date(normalizeUtcDateStr(dateStr));
    if (isNaN(date.getTime())) return '';

    const utcFormatted = date.toLocaleString('en-US', { ...DATE_TIME_OPTS, timeZone: 'UTC' });

    if (isTenantUtc()) {
        return `${utcFormatted} UTC`;
    }

    const tenantFormatted = date.toLocaleString('en-US', { ...DATE_TIME_OPTS, timeZone: _tenantTimeZone });
    const tenantAbbr = getTzAbbr(date, _tenantTimeZone);

    // If they render identically (shouldn't happen if TZ differs, but guard)
    if (tenantFormatted === utcFormatted) return `${utcFormatted} UTC`;

    return `${tenantFormatted} ${tenantAbbr}<span class="dt-secondary">${utcFormatted} UTC</span>`;
}

/**
 * Plain-text version of formatDateTime (no HTML).
 * Used for contexts that cannot render HTML (e.g., table search/filter, tooltips).
 */
export function formatDateTimePlain(dateStr) {
    if (!dateStr) return '';
    const date = new Date(normalizeUtcDateStr(dateStr));
    if (isNaN(date.getTime())) return '';

    const utcFormatted = date.toLocaleString('en-US', { ...DATE_TIME_OPTS, timeZone: 'UTC' });

    if (isTenantUtc()) {
        return `${utcFormatted} UTC`;
    }

    const tenantFormatted = date.toLocaleString('en-US', { ...DATE_TIME_OPTS, timeZone: _tenantTimeZone });
    const tenantAbbr = getTzAbbr(date, _tenantTimeZone);

    if (tenantFormatted === utcFormatted) return `${utcFormatted} UTC`;
    return `${tenantFormatted} ${tenantAbbr} (${utcFormatted} UTC)`;
}

/**
 * Format a date-only value (e.g., expiration dates).
 * Returns "M/D/YYYY" using the tenant timezone if set, otherwise UTC.
 */
export function formatDateOnly(dateStr) {
    if (!dateStr) return '';
    const date = new Date(normalizeUtcDateStr(dateStr));
    if (isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-US', {
        year: 'numeric', month: 'numeric', day: 'numeric',
        timeZone: _tenantTimeZone || 'UTC'
    });
}
